<script setup lang="ts">
import {
  SigningFileType,
  SigningRequestSigneeRole,
  SigningRequestStatus,
  WebsocketSubscriptionTarget,
  type ContractManagementContractFileInDb,
  type ParticipantInDb,
  type SignedVaultFileRevisionInDb,
  type SigningRequestInDb,
  type SigningRequestStatusEntry,
  type VaultFileRevisionInDb,
  type VaultFileSigningRequestInDb,
} from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaAckList, { type GaiaAckItem } from '@/components/design/composite/GaiaAckList.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaChoiceCard from '@/components/design/base/GaiaChoiceCard.vue';
import GaiaConfirmCard from '@/components/design/composite/GaiaConfirmCard.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaParticipantRow from '@/components/design/signing/GaiaParticipantRow.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTimeline, {
  type GaiaTimelineItem,
} from '@/components/design/composite/GaiaTimeline.vue';
import {
  participantStatusMap,
  signedParticipantStates,
  signeeRoleMap,
  signatureTypeMap,
  signingFileTypeMap,
  signingRequestStatusMap,
} from '@/components/design/statusMaps';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import { SignatureRequestService } from '@/services/contractManagement/signatureRequestService';
import {
  fetchParticipants,
  fetchSigningVaultFiles,
} from '@/services/integration/signatureFieldService';
import { useAuthStore } from '@/stores/auth';
import { initialsOf } from '@/utils/initials';
import { logger } from '@/utils/logger';
import { isAccessDenied } from '@/utils/errorHandler';
import {
  participantName,
  participantOrganisation,
  participantsInRole,
} from '@/utils/signingParticipant';
import { IconDocument, IconDownload, IconEye, IconMore } from '@/components/design/icons';
import {
  NBreadcrumbItem,
  NFlex,
  NGi,
  NGrid,
  NIcon,
  NSpin,
  NTooltip,
  type DataTableColumns,
  type DropdownOption,
} from 'naive-ui';
import {
  computed,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Component,
  type VNode,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useSigningRequestWebSocket } from '@/composables/useSigningRequestWebSocket';
import { formatExpiryDay } from '@/composables/useSigningWizard';
import { contractFileRoute, contractRoute, signingRequestEditRoute } from '@/router/links';

/**
 * The rebuilt signature-request detail (Signature Request Detail mock):
 * overview beside the participants and the activity trail, documents below.
 * Everything the old page could do is still here — the actions that don't fit
 * the mock's five buttons live in the header's ⋯ menu.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();

const loading = ref(true);
/** The backend refused the request: deleted, no access, or another organisation's. */
const unavailable = ref(false);
const signingRequest = ref<SigningRequestInDb | null>(null);
const statusHistory = ref<SigningRequestStatusEntry[]>([]);
const hasStatusHistoryResponse = ref(false);
const participants = ref<ParticipantInDb[]>([]);
const vaultFiles = ref<VaultFileSigningRequestInDb[]>([]);
const signedDocuments = ref<SignedVaultFileRevisionInDb[]>([]);

const cancelling = ref(false);
const deletingDraft = ref(false);
const updatingStatus = ref(false);
const togglingArchive = ref(false);
const remindingAll = ref(false);
const downloadingSigned = ref(false);
const downloadingAuditTrail = ref(false);
const downloadingRevisionUid = ref<string | null>(null);
const sendingReminderForUid = ref<string | null>(null);

const confirmingCancel = ref(false);
const confirmingDelete = ref(false);
const previewRevision = ref<VaultFileRevisionInDb | null>(null);
const downloadModalOpen = ref(false);
const downloadMode = ref<'separate' | 'combined'>('separate');
const combinedAcknowledged = ref(false);

const signingRequestUid = computed(() =>
  typeof route.params.signingRequestUid === 'string' ? route.params.signingRequestUid : ''
);

// ── Derived state ───────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
const requestName = computed(() => signingRequest.value?.name?.trim() || 'Unnamed request');
const status = computed(() => signingRequest.value?.status ?? null);
const isDraft = computed(() => status.value === SigningRequestStatus.DRAFT);

// ── Just sent ───────────────────────────────────────────────────────────────
//
// Sending only queues the hand-off to the provider; a worker moves the request
// out of draft seconds later and the websocket below brings that in. Until
// then, a page opened by the wizard's Send (`?sent=1`) reads as on its way —
// not as a draft to edit.

/** Long enough for a healthy hand-off; past it, something is wrong and the page says so. */
const SEND_WAIT_LIMIT_MS = 60_000;

const awaitingSend = ref(route.query.sent === '1');
let sendWaitTimer: number | null = null;

const sending = computed(
  () =>
    awaitingSend.value &&
    (status.value === SigningRequestStatus.DRAFT || status.value === SigningRequestStatus.PENDING)
);

function stopAwaitingSend(): void {
  if (sendWaitTimer !== null) window.clearTimeout(sendWaitTimer);
  sendWaitTimer = null;
  awaitingSend.value = false;
  // A refresh later is an ordinary visit, not another wait.
  if (route.query.sent) {
    const query = { ...route.query };
    delete query.sent;
    void router.replace({ query });
  }
}

/** Once loaded, wait for the request to move — or give up after the limit and say so. */
function startAwaitingSend(): void {
  if (!sending.value) {
    stopAwaitingSend();
    return;
  }
  sendWaitTimer = window.setTimeout(() => {
    stopAwaitingSend();
    message.warning('Sending is taking longer than usual. Refresh in a moment to see its status.');
  }, SEND_WAIT_LIMIT_MS);
}

// The status moved on (a websocket refresh brought it in): the wait is over.
watch(status, (_next, previous) => {
  if (awaitingSend.value && previous !== null && !sending.value) stopAwaitingSend();
});

onBeforeUnmount(() => {
  if (sendWaitTimer !== null) window.clearTimeout(sendWaitTimer);
});
const isErrorStatus = computed(() => status.value === SigningRequestStatus.ERROR);
/** A draft never reached the provider, so it is deleted rather than cancelled. */
const canCancel = computed(() => status.value === SigningRequestStatus.WAITING_FOR_SIGNATURES);
const canDownloadSigned = computed(() => status.value === SigningRequestStatus.SIGNED);
const canUpdateStatus = computed(
  () => authStore.userPermissions.is_platform_admin && hasStatusHistoryResponse.value
);
const contractUid = computed(() => signingRequest.value?.contract?.uid ?? null);
const contractFiles = computed(() => signingRequest.value?.contract_files ?? []);

const sortedStatusHistory = computed(() =>
  [...statusHistory.value].sort(
    (left, right) =>
      (left.changed_at ? new Date(left.changed_at).getTime() : 0) -
      (right.changed_at ? new Date(right.changed_at).getTime() : 0)
  )
);

/**
 * The trail's wording for what happened, where it differs from the status chip:
 * the chip says what the request *is* ("Draft", "Awaiting signature"), the
 * trail what someone *did* (created it, sent it for signature).
 */
const timelineLabels: Partial<Record<SigningRequestStatus, string>> = {
  [SigningRequestStatus.DRAFT]: 'Created',
  [SigningRequestStatus.WAITING_FOR_SIGNATURES]: 'Sent for signature',
};

/**
 * Who made the change — the backend credits what a user did here (created
 * the draft, sent it, cancelled it); what the provider reports carries no user.
 */
function statusActor(entry: SigningRequestStatusEntry): string | undefined {
  const user = entry.changed_by_user;
  if (!user) return undefined;
  return `by ${user.full_name.trim() || user.email}`;
}

const timelineItems = computed<GaiaTimelineItem[]>(() =>
  sortedStatusHistory.value.map((entry, index) => ({
    key: `${entry.changed_at ?? 'unknown'}-${entry.status ?? 'unknown'}-${index}`,
    label: entry.status
      ? (timelineLabels[entry.status] ?? signingRequestStatusMap[entry.status].label)
      : 'Unknown status',
    detail: statusActor(entry),
    time: formatDateTime(entry.changed_at),
    // Every entry in the history already happened; the trail is a record, not
    // a checklist, so the whole thing reads as done.
    done: true,
  }))
);

// ── Participants ────────────────────────────────────────────────────────────

/**
 * Reminders make sense for signers and approvers who still owe something —
 * `participantStatusMap` decides "still owes" so the rule has one definition.
 * A follower is only ever copied in, so they are never reminded.
 */
function canRemind(participant: ParticipantInDb): boolean {
  if (participant.role === SigningRequestSigneeRole.FOLLOWER || !participant.status) {
    return false;
  }

  return participantStatusMap[participant.status].canRemind;
}

const remindableParticipants = computed(() => participants.value.filter(canRemind));

/** The role line under a participant's name, numbered when the order matters. */
function participantDetail(participant: ParticipantInDb): string {
  const ordered =
    (participant.role === SigningRequestSigneeRole.SIGNER &&
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR, not a nullish fallback
      signingRequest.value?.ordered_signers) ||
    (participant.role === SigningRequestSigneeRole.APPROVER &&
      signingRequest.value?.ordered_approvers);

  const position = participantsInRole(participants.value, participant.role).findIndex(
    entry => entry.uid === participant.uid
  );

  const role = ordered
    ? `${position + 1}. ${signeeRoleMap[participant.role]}`
    : signeeRoleMap[participant.role];

  return [
    role,
    participant.signature_type ? signatureTypeMap[participant.signature_type] : null,
    participantOrganisation(participant) || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const signedCountLabel = computed(() => {
  const signers = participantsInRole(participants.value, SigningRequestSigneeRole.SIGNER);
  if (signers.length === 0) {
    return `${participants.value.length} participants`;
  }

  const signed = signers.filter(
    participant => participant.status && signedParticipantStates.has(participant.status)
  ).length;

  return `${signed}/${signers.length} signed`;
});

// ── Formatting ──────────────────────────────────────────────────────────────

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';

  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The provider arrives as a raw backend string. */
function humanize(value: string | null | undefined): string {
  if (!value) return '—';

  return value.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

function providerLabel(provider: string | null | undefined): string {
  switch (provider) {
    case 'gaia_youtrust':
      return 'GAIA (Youtrust)';
    case 'youtrust':
    case 'youtrust_integration':
      return 'Youtrust (own account)';
    default:
      return humanize(provider);
  }
}

function sanitizeFileNameBase(rawName: string | null | undefined): string | null {
  const sanitized = rawName
    ?.trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim();

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
  return sanitized || null;
}

// ── Documents ───────────────────────────────────────────────────────────────

const contractFilesByVaultFileUid = computed(
  () =>
    new Map(
      contractFiles.value
        .map(contractFile =>
          contractFile.vault_file ? ([contractFile.vault_file.uid, contractFile] as const) : null
        )
        .filter(
          (entry): entry is readonly [string, ContractManagementContractFileInDb] => entry !== null
        )
    )
);

/**
 * The contract file a signing document came from, so the row can link back to
 * it. A single-file contract is matched by position when the vault-file uid
 * doesn't line up — the old page's rule, kept.
 */
function resolveContractFile(
  file: VaultFileSigningRequestInDb
): ContractManagementContractFileInDb | null {
  if (contractFiles.value.length === 0) {
    return null;
  }

  const matched = contractFilesByVaultFileUid.value.get(file.vault_file.uid);
  if (matched) {
    return matched;
  }

  return file.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT &&
    contractFiles.value.length === 1
    ? contractFiles.value[0]
    : null;
}

interface DocumentItem {
  uid: string;
  name: string;
  signingFileType: SigningFileType;
  contractFile: ContractManagementContractFileInDb | null;
  revision: VaultFileRevisionInDb;
}

const documentItems = computed<DocumentItem[]>(() =>
  vaultFiles.value.flatMap(file => {
    const revision = file.vault_file.current_revision;
    if (!revision) {
      return [];
    }

    return [
      {
        uid: revision.uid,
        name: revision.file_name.trim() || 'Unnamed document',
        signingFileType: file.signing_file_type,
        contractFile: resolveContractFile(file),
        revision,
      },
    ];
  })
);

function iconAction(label: string, icon: Component, onClick: () => void, busy = false): VNode {
  const button = h(
    GaiaIconButton,
    { quaternary: true, dimension: 28, loading: busy, 'aria-label': label, onClick },
    () => h(NIcon, { component: icon })
  );

  return h(
    NTooltip,
    { trigger: 'hover', placement: 'top' },
    { trigger: () => button, default: () => label }
  );
}

const documentColumns = computed((): DataTableColumns<DocumentItem> => [
  {
    title: 'Document',
    key: 'name',
    minWidth: 260,
    render: (row): VNode =>
      h(NFlex, { align: 'center', size: 10, wrap: false, style: { minWidth: 0 } }, () => [
        h(NIcon, { component: IconDocument, size: 15, depth: 4 }),
        h(
          GaiaText,
          { size: 'item', weight: 'semibold', truncate: true, title: row.name },
          () => row.name
        ),
      ]),
  },
  {
    title: 'Signing file type',
    key: 'signingFileType',
    width: 180,
    render: (row): VNode =>
      h(GaiaTag, { size: 'small' }, () => signingFileTypeMap[row.signingFileType]),
  },
  {
    title: '',
    key: 'actions',
    width: 130,
    align: 'right',
    render: (row): VNode =>
      h(NFlex, { align: 'center', size: 6, wrap: false, justify: 'end' }, () =>
        [
          row.contractFile
            ? iconAction('Open contract file', IconDocument, () => {
                openContractFile(row.contractFile?.uid ?? null);
              })
            : null,
          iconAction('Preview', IconEye, () => (previewRevision.value = row.revision)),
          iconAction(
            'Download',
            IconDownload,
            () => void downloadRevision(row.revision),
            downloadingRevisionUid.value === row.revision.uid
          ),
        ].filter(Boolean)
      ),
  },
]);

// ── Signed-document download ────────────────────────────────────────────────

const signableSignedDocuments = computed(() =>
  signedDocuments.value.filter(
    document => document.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT
  )
);

/** One signed file downloads straight away; a set of them needs the choice. */
const needsDownloadModal = computed(() => {
  const signable = signableSignedDocuments.value.length;
  const attachments = signedDocuments.value.length - signable;

  return signable > 1 || (signable >= 1 && attachments > 0);
});

const combinedAckItems = computed<GaiaAckItem[]>(() => [
  {
    key: 'combined',
    checked: combinedAcknowledged.value,
    text: 'I understand the combined PDF will not carry a valid cryptographic signature, and that the separate signed files remain individually validatable.',
  },
]);

const canConfirmDownload = computed(
  () => downloadMode.value === 'separate' || combinedAcknowledged.value
);

// ── Header actions ──────────────────────────────────────────────────────────

const overflowOptions = computed<DropdownOption[]>(() => [
  ...(contractUid.value ? [{ label: 'Open contract', key: 'contract' }] : []),
  ...(canUpdateStatus.value ? [{ label: 'Update status', key: 'status' }] : []),
  {
    label: signingRequest.value?.archived ? 'Unarchive request' : 'Archive request',
    key: 'archive',
  },
  ...(canDownloadSigned.value ? [{ label: 'Download audit trail', key: 'audit' }] : []),
  ...(isDraft.value
    ? [
        { type: 'divider', key: 'sep' },
        { label: 'Delete draft', key: 'delete', props: gaiaDangerOption },
      ]
    : []),
]);

function handleOverflowSelect(key: string): void {
  switch (key) {
    case 'contract':
      openContract();
      return;
    case 'status':
      void updateStatusFromProvider();
      return;
    case 'archive':
      void toggleArchive();
      return;
    case 'audit':
      void downloadAuditTrail();
      return;
    case 'delete':
      confirmingDelete.value = true;
      return;
  }
}

// ── Navigation ──────────────────────────────────────────────────────────────

function openContract(): void {
  if (!contractUid.value) return;

  void router.push(contractRoute(contractUid.value));
}

function openContractFile(contractFileUid: string | null): void {
  if (!contractFileUid) return;

  void router.push(contractFileRoute(contractFileUid));
}

function editDraft(): void {
  void router.push(signingRequestEditRoute(signingRequestUid.value));
}

// ── Data ────────────────────────────────────────────────────────────────────

async function refreshDetail(): Promise<void> {
  const [request, history, parties, files] = await Promise.all([
    SignatureRequestService.get(signingRequestUid.value),
    SignatureRequestService.getStatusHistory(signingRequestUid.value),
    fetchParticipants(signingRequestUid.value),
    fetchSigningVaultFiles(signingRequestUid.value),
  ]);

  signingRequest.value = request;
  hasStatusHistoryResponse.value = history !== null;
  statusHistory.value = history ?? [];
  participants.value = parties;
  vaultFiles.value = files;

  if (request.status === SigningRequestStatus.SIGNED) {
    try {
      signedDocuments.value = await SignatureRequestService.getSignedDocuments(
        signingRequestUid.value
      );
    } catch (error) {
      signedDocuments.value = [];
      logger.error('Failed to load signed document revisions:', error);
      message.warning('Signed document revisions could not be loaded.');
    }
  } else {
    signedDocuments.value = [];
  }
}

async function loadDetail(): Promise<void> {
  if (!signingRequestUid.value) {
    message.error('Missing signing request UID.');
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    await refreshDetail();
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Signing request not available:', error);
      unavailable.value = true;
      return;
    }
    logger.error('Failed to load signing request detail:', error);
    message.error(error instanceof Error ? error.message : 'Failed to load signing request.');
  } finally {
    loading.value = false;
  }
}

// ── Mutations ───────────────────────────────────────────────────────────────

/**
 * Error messages from the provider embed ISO timestamps ("next reminder
 * allowed after …"); showing them in the user's own format keeps the message
 * readable.
 */
function localizeTimestamps(text: string): string {
  return text.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})/g, iso =>
    formatDateTime(iso)
  );
}

async function sendReminder(participant: ParticipantInDb): Promise<void> {
  sendingReminderForUid.value = participant.uid;

  try {
    const updated = await SignatureRequestService.sendParticipantReminder(
      signingRequestUid.value,
      participant.uid
    );
    participants.value = participants.value.map(entry =>
      entry.uid === updated.uid ? updated : entry
    );
    message.success('Reminder sent.');
  } catch (error) {
    logger.error('Failed to send participant reminder:', error);
    message.error(
      localizeTimestamps(error instanceof Error ? error.message : 'Failed to send reminder.')
    );
  } finally {
    sendingReminderForUid.value = null;
  }
}

async function remindAll(): Promise<void> {
  const open = remindableParticipants.value;
  if (open.length === 0) return;

  remindingAll.value = true;

  try {
    for (const participant of open) {
      const updated = await SignatureRequestService.sendParticipantReminder(
        signingRequestUid.value,
        participant.uid
      );
      participants.value = participants.value.map(entry =>
        entry.uid === updated.uid ? updated : entry
      );
    }
    message.success(
      open.length === 1 ? 'Reminder sent.' : `Reminders sent to ${open.length} participants.`
    );
  } catch (error) {
    logger.error('Failed to send reminders:', error);
    message.error(
      localizeTimestamps(error instanceof Error ? error.message : 'Failed to send reminders.')
    );
  } finally {
    remindingAll.value = false;
  }
}

async function toggleArchive(): Promise<void> {
  if (!signingRequest.value) return;

  const nextArchived = !signingRequest.value.archived;
  togglingArchive.value = true;

  try {
    await SignatureRequestService.setArchived(signingRequestUid.value, nextArchived);
    await refreshDetail();
    message.success(nextArchived ? 'Signature request archived.' : 'Signature request unarchived.');
  } catch (error) {
    logger.error('Failed to update signature request:', error);
    message.error(error instanceof Error ? error.message : 'Failed to update signature request.');
  } finally {
    togglingArchive.value = false;
  }
}

/** Pulls the live state from the signing provider (platform admins only). */
async function updateStatusFromProvider(): Promise<void> {
  if (!canUpdateStatus.value) return;

  updatingStatus.value = true;

  try {
    await SignatureRequestService.getProvider(signingRequestUid.value);
    await refreshDetail();
    message.success('Signature request updated.');
  } catch (error) {
    logger.error('Failed to update signature request status:', error);
    message.error(error instanceof Error ? error.message : 'Failed to update status.');
  } finally {
    updatingStatus.value = false;
  }
}

async function cancelRequest(): Promise<void> {
  if (!canCancel.value) return;

  cancelling.value = true;

  try {
    await SignatureRequestService.cancel(signingRequestUid.value, { reason: 'other' });
    await refreshDetail();
    confirmingCancel.value = false;
    message.success('Signature request cancelled.');
  } catch (error) {
    logger.error('Failed to cancel signature request:', error);
    message.error(error instanceof Error ? error.message : 'Failed to cancel signature request.');
  } finally {
    cancelling.value = false;
  }
}

async function deleteDraft(): Promise<void> {
  if (!isDraft.value) return;

  deletingDraft.value = true;

  try {
    await SignatureRequestService.remove(signingRequestUid.value);
    message.success('Draft signature request deleted.');
    await router.push({ name: 'signing-requests' });
  } catch (error) {
    logger.error('Failed to delete draft signature request:', error);
    message.error(error instanceof Error ? error.message : 'Failed to delete draft.');
  } finally {
    deletingDraft.value = false;
  }
}

// ── Downloads ───────────────────────────────────────────────────────────────

async function downloadRevision(revision: VaultFileRevisionInDb): Promise<void> {
  downloadingRevisionUid.value = revision.uid;

  try {
    const blob = await ContractFileDownloadService.downloadVaultFile(
      revision.uid,
      revision.mime_type
    );
    ContractFileDownloadService.triggerDownload(blob, revision.file_name);
    message.success('Document download started.');
  } catch (error) {
    logger.error('Failed to download document revision:', error);
    message.error(error instanceof Error ? error.message : 'Failed to download document.');
  } finally {
    downloadingRevisionUid.value = null;
  }
}

function startSignedDownload(): void {
  if (!canDownloadSigned.value) return;

  if (signableSignedDocuments.value.length === 0) {
    message.warning('No signed documents are available for this signature request.');
    return;
  }

  if (!needsDownloadModal.value) {
    void downloadSeparateFiles(signableSignedDocuments.value);
    return;
  }

  downloadMode.value = 'separate';
  combinedAcknowledged.value = false;
  downloadModalOpen.value = true;
}

async function downloadSeparateFiles(documents: SignedVaultFileRevisionInDb[]): Promise<boolean> {
  downloadingSigned.value = true;

  try {
    for (const document of documents) {
      const revision = document.signed_vault_file_revision;
      const blob = await ContractFileDownloadService.downloadVaultFile(
        revision.uid,
        revision.mime_type
      );
      ContractFileDownloadService.triggerDownload(blob, revision.file_name);
    }

    message.success(
      documents.length === 1
        ? 'Signed document download started.'
        : `${documents.length} signed document downloads started.`
    );
    return true;
  } catch (error) {
    logger.error('Failed to download signed documents:', error);
    message.error(error instanceof Error ? error.message : 'Failed to download signed documents.');
    return false;
  } finally {
    downloadingSigned.value = false;
  }
}

async function downloadCombinedFile(): Promise<boolean> {
  downloadingSigned.value = true;

  try {
    const blob = await SignatureRequestService.downloadCombinedSignedDocument(
      signingRequestUid.value,
      true
    );
    const base = sanitizeFileNameBase(signingRequest.value?.name);
    ContractFileDownloadService.triggerDownload(
      blob,
      base ? `${base}-signed.pdf` : 'signed-documents.pdf'
    );
    message.success('Combined signed document download started.');
    return true;
  } catch (error) {
    logger.error('Failed to download combined signed document:', error);
    message.error(error instanceof Error ? error.message : 'Failed to download combined document.');
    return false;
  } finally {
    downloadingSigned.value = false;
  }
}

async function confirmDownload(): Promise<void> {
  if (!canConfirmDownload.value || downloadingSigned.value) return;

  const success =
    downloadMode.value === 'combined'
      ? await downloadCombinedFile()
      : await downloadSeparateFiles(signedDocuments.value);

  if (success) {
    downloadModalOpen.value = false;
  }
}

async function downloadAuditTrail(): Promise<void> {
  if (!canDownloadSigned.value) return;

  downloadingAuditTrail.value = true;

  try {
    const blob = await SignatureRequestService.downloadAuditTrail(signingRequestUid.value);
    const base = sanitizeFileNameBase(signingRequest.value?.name);
    ContractFileDownloadService.triggerDownload(
      blob,
      base ? `${base}-audit-trail.pdf` : 'audit-trail.pdf'
    );
    message.success('Audit trail download started.');
  } catch (error) {
    logger.error('Failed to download audit trail:', error);
    message.error(error instanceof Error ? error.message : 'Failed to download audit trail.');
  } finally {
    downloadingAuditTrail.value = false;
  }
}

function handlePreviewVisibility(visible: boolean): void {
  if (!visible) {
    previewRevision.value = null;
  }
}

function handleDownloadModalVisibility(visible: boolean): void {
  if (!downloadingSigned.value) {
    downloadModalOpen.value = visible;
  }
}

onMounted(async () => {
  await loadDetail();
  if (awaitingSend.value) startAwaitingSend();
});

// The hand-off to the provider, signers signing, a cancel elsewhere: every change
// to this request is pushed, and the page refetches it.
useSigningRequestWebSocket({
  target: WebsocketSubscriptionTarget.SIGNING_REQUEST,
  uid: () => signingRequestUid.value || null,
  onUpdated: () => {
    refreshDetail().catch((error: unknown) => {
      logger.warn('Refreshing the signing request after an update failed:', error);
    });
  },
});
</script>

<template>
  <layout content-max-width="1320px">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.sign }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'signing-requests' }">{{
          navLabels.signatureRequests
        }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item v-if="!unavailable">{{ requestName }}</n-breadcrumb-item>
    </template>

    <!-- The request is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable">
      <template #actions>
        <gaia-button type="primary" size="small" @click="router.push({ name: 'signing-requests' })">
          Back to {{ navLabels.signatureRequests.toLowerCase() }}
        </gaia-button>
      </template>
    </gaia-not-found>
    <n-spin v-else :show="loading">
      <!-- The mock stacks the body at 22px, but that was measured against an
           h1 with no margin of its own — see GaiaHeader. 18 keeps the header
           close to the cards it introduces. -->
      <n-flex vertical :size="18" :wrap="false">
        <!-- Title, live status, and the actions. -->
        <n-flex align="flex-start" :size="16">
          <n-flex
            align="center"
            :size="12"
            :wrap="false"
            :style="{ flex: '1 1 300px', minWidth: 0 }"
          >
            <gaia-header :level="1">{{ requestName }}</gaia-header>
            <gaia-status-chip v-if="sending" size="medium" tone="accent">Sending…</gaia-status-chip>
            <gaia-status-chip
              v-else-if="status"
              size="medium"
              :tone="signingRequestStatusMap[status].tone"
            >
              {{ signingRequestStatusMap[status].label }}
            </gaia-status-chip>
            <gaia-tag v-if="signingRequest?.archived" variant="muted">Archived</gaia-tag>
          </n-flex>

          <n-flex
            align="center"
            :size="8"
            justify="end"
            :style="{ marginLeft: 'auto', flex: 'none' }"
          >
            <gaia-button
              v-if="isDraft && !sending"
              type="primary"
              :disabled="loading"
              @click="editDraft"
            >
              Edit draft
            </gaia-button>
            <gaia-button
              v-if="remindableParticipants.length > 0"
              :loading="remindingAll"
              @click="remindAll"
            >
              Remind all
            </gaia-button>
            <gaia-button
              v-if="canDownloadSigned"
              type="primary"
              :loading="downloadingSigned"
              @click="startSignedDownload"
            >
              <template #icon><n-icon :component="IconDownload" /></template>
              Download signed
            </gaia-button>
            <gaia-button
              v-if="canCancel"
              type="error"
              ghost
              :disabled="confirmingCancel"
              @click="confirmingCancel = true"
            >
              Cancel request
            </gaia-button>
            <gaia-dropdown :options="overflowOptions" @select="handleOverflowSelect">
              <gaia-icon-button
                :dimension="34"
                :loading="togglingArchive || updatingStatus || downloadingAuditTrail"
                aria-label="More actions"
              >
                <n-icon :component="IconMore" />
              </gaia-icon-button>
            </gaia-dropdown>
          </n-flex>
        </n-flex>

        <gaia-banner v-if="isErrorStatus && signingRequest?.status_message" type="error">
          {{ signingRequest.status_message }}
        </gaia-banner>

        <gaia-confirm-card
          v-if="confirmingCancel"
          confirm-label="Cancel request"
          cancel-label="Keep open"
          @confirm="cancelRequest"
          @cancel="confirmingCancel = false"
        >
          <gaia-text size="body" tone="text">
            Signers lose access to their signing links immediately. Already-collected signatures are
            void.
          </gaia-text>
        </gaia-confirm-card>

        <gaia-confirm-card
          v-if="confirmingDelete"
          confirm-label="Delete draft"
          @confirm="deleteDraft"
          @cancel="confirmingDelete = false"
        >
          Delete this draft? Its recipients and documents are discarded too. Nothing was sent, so no
          signer is affected.
        </gaia-confirm-card>

        <n-grid cols="1 900:5" :x-gap="18" :y-gap="18" item-responsive>
          <!-- Overview -->
          <n-gi span="1 900:3">
            <gaia-card :segmented="{ content: true }">
              <template #header><gaia-section-header>Overview</gaia-section-header></template>
              <template #header-extra>
                <gaia-text size="micro" tone="muted">{{ signingRequestUid }}</gaia-text>
              </template>

              <!-- The mock's two-column read-out. `n-grid` measures its breakpoints
                   against its own width (naive's `responsive` defaults to "self"),
                   and this card is only ~700px wide inside the page grid — so the
                   cells are `stacked` field rows, whose label sits above the value
                   and needs no 300px+ track of its own. -->
              <n-grid cols="1 460:2" :x-gap="28" :y-gap="18">
                <n-gi>
                  <gaia-field-row variant="stacked" label="Provider">
                    {{ providerLabel(signingRequest?.provider) }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Signing order">
                    {{
                      signingRequest?.ordered_signers ? 'One after another' : 'All at the same time'
                    }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Approval order">
                    {{
                      signingRequest?.ordered_approvers
                        ? 'One after another, before signing'
                        : 'All at the same time, before signing'
                    }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Created">
                    {{ formatDateTime(signingRequest?.created_at) }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Last updated">
                    {{ formatDateTime(signingRequest?.updated_at) }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Expires">
                    {{ formatExpiryDay(signingRequest?.expiration_date) }}
                  </gaia-field-row>
                </n-gi>
                <n-gi>
                  <gaia-field-row variant="stacked" label="Contract">
                    {{ signingRequest?.contract?.name ?? '—' }}
                  </gaia-field-row>
                </n-gi>
                <n-gi v-if="signingRequest?.status_message && !isErrorStatus">
                  <gaia-field-row variant="stacked" label="Status detail">
                    {{ signingRequest.status_message }}
                  </gaia-field-row>
                </n-gi>
              </n-grid>

              <gaia-sub-panel label="Message to signers" :style="{ marginTop: '16px' }">
                <gaia-text size="body" tone="text" :style="{ lineHeight: 1.55 }">
                  {{
                    signingRequest?.message?.trim() ||
                    'No message — recipients get the default signing email.'
                  }}
                </gaia-text>
              </gaia-sub-panel>
            </gaia-card>
          </n-gi>

          <!-- Activity beside the overview, stretched to the row so both cards end level. -->
          <n-gi span="1 900:2">
            <gaia-card :segmented="{ content: true }" style="height: 100%">
              <template #header><gaia-section-header>Activity</gaia-section-header></template>

              <gaia-empty-state
                v-if="!loading && timelineItems.length === 0"
                title="No status history"
              >
                The provider has not reported anything for this request yet.
              </gaia-empty-state>
              <n-scrollbar v-else style="max-height: 360px">
                <gaia-timeline :items="timelineItems" />
              </n-scrollbar>
            </gaia-card>
          </n-gi>
        </n-grid>

        <!-- Participants full width, like the documents below: long names and
             party labels fit, and a long list grows the page instead of one column. -->
        <gaia-card :segmented="{ content: true }" content-style="padding: 0">
          <template #header><gaia-section-header>Recipients</gaia-section-header></template>
          <template #header-extra>
            <gaia-text size="control" tone="subtle">{{ signedCountLabel }}</gaia-text>
          </template>

          <gaia-empty-state v-if="!loading && participants.length === 0" title="No recipients yet">
            Recipients are added in the wizard's third step.
          </gaia-empty-state>
          <template v-else>
            <gaia-participant-row
              v-for="participant in participants"
              :key="participant.uid"
              :name="participantName(participant)"
              :detail="participantDetail(participant)"
              :reminder="
                participant.last_manual_reminder_sent_at
                  ? `Reminded ${formatDateTime(participant.last_manual_reminder_sent_at)}`
                  : undefined
              "
              :initials="initialsOf(participantName(participant))"
              :tone="participant.status ? participantStatusMap[participant.status].tone : 'neutral'"
              :state-label="
                participant.status
                  ? participantStatusMap[participant.status].label
                  : 'Not yet notified'
              "
              :can-remind="canRemind(participant) && sendingReminderForUid === null"
              @remind="sendReminder(participant)"
            />
          </template>
        </gaia-card>

        <gaia-table
          :columns="documentColumns"
          :data="documentItems"
          :row-key="(row: DocumentItem) => row.uid"
        >
          <template #empty>
            <gaia-empty-state title="No documents attached">
              A signature request needs at least one signable document.
            </gaia-empty-state>
          </template>
        </gaia-table>
      </n-flex>
    </n-spin>

    <!-- Document preview -->
    <gaia-modal
      :show="previewRevision !== null"
      width="900px"
      :title="previewRevision?.file_name ?? 'Document preview'"
      @update:show="handlePreviewVisibility"
    >
      <div :style="{ height: '70vh', overflow: 'auto' }">
        <gaia-vault-file-pdf-preview
          v-if="previewRevision"
          :revision-uid="previewRevision.uid"
          :revision-preview-status="previewRevision.preview_status ?? null"
        />
      </div>
    </gaia-modal>

    <!-- Signed-document download: separate files keep their own signature, a
         combined PDF does not — hence the acknowledgement gate. -->
    <gaia-modal
      :show="downloadModalOpen"
      width="520px"
      title="Download signed documents"
      :mask-closable="!downloadingSigned"
      :close-on-esc="!downloadingSigned"
      @update:show="handleDownloadModalVisibility"
    >
      <n-flex vertical :size="12" :wrap="false">
        <gaia-choice-card
          label="Separate files (incl. attachments)"
          meta="Each signed document and attachment as its own file. Per-file cryptographic signatures stay intact and individually verifiable."
          :selected="downloadMode === 'separate'"
          @select="downloadMode = 'separate'"
        />
        <gaia-choice-card
          label="Combined PDF"
          meta="All signed documents merged into a single PDF."
          :selected="downloadMode === 'combined'"
          @select="downloadMode = 'combined'"
        />
        <gaia-ack-list
          v-if="downloadMode === 'combined'"
          type="warning"
          title="Combining clears the cryptographic signature of the merged file"
          :items="combinedAckItems"
          :disabled="downloadingSigned"
          @toggle="(_key, checked) => (combinedAcknowledged = checked)"
        >
          Merging produces a new PDF whose combined cryptographic signature is not valid. The
          original separate files are unaffected and remain individually validatable.
        </gaia-ack-list>
      </n-flex>

      <template #action>
        <n-flex :size="8" :wrap="false" justify="end">
          <gaia-button :disabled="downloadingSigned" @click="downloadModalOpen = false">
            Cancel
          </gaia-button>
          <gaia-button
            type="primary"
            :disabled="!canConfirmDownload"
            :loading="downloadingSigned"
            @click="confirmDownload"
          >
            <template #icon><n-icon :component="IconDownload" /></template>
            Download
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </layout>
</template>
